import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';

/**
 * Generischer Tabellen-Editor für Listen, die KEIN dediziertes UI brauchen.
 *
 * Deckt zwei Formen ab:
 * - **String-Listen** (z.B. Bodenvegetationen): einspaltige Bearbeitung.
 * - **Objekt-Listen** (z.B. Neigungsstufe, Reviere): Spalten werden aus den
 *   Daten inferiert; jede Zelle ist editierbar.
 *
 * Großen Katalogen (Reviere ~3000, Bodenvegetationen ~10000) gerecht:
 * Suche + sichtbar begrenzte Render-Anzahl (kein stilles Kappen) + Einzel-Edit,
 * Massenpflege über JSON-Import. Der Editor mutiert das übergebene `data`-Array
 * IN PLACE und meldet Änderungen via `changed` — die Eltern-Komponente speichert
 * dann über ihren bestehenden „Speichern"-Button in die zentrale Listen-DB.
 */
@Component({
  standalone: false,
  selector: 'app-generic-list-editor',
  styles: [`
    /* Füllt die verfügbare Höhe im Editor: Flex-Spalte, Toolbar oben fix,
       Tabelle wächst und scrollt intern. Die definite Höhe kommt vom Eltern-
       Flex (.lv-editor-fill in der Listenverwaltung); flex:1 fängt sie auf. */
    :host {
      display: flex;
      flex-direction: column;
      flex: 1 1 auto;
      min-height: 0;
    }
    .gle-scroll {
      flex: 1 1 auto;
      min-height: 0;
      overflow: auto;
    }
  `],
  template: `
    <div class="d-flex align-items-center flex-wrap gap-2 mb-2">
      <div class="input-group input-group-sm" style="max-width: 320px;">
        <span class="input-group-text"><i class="bi bi-search"></i></span>
        <input type="text" class="form-control" [(ngModel)]="searchTerm"
               (ngModelChange)="recompute()" placeholder="Suchen…">
      </div>
      @if (!readonly) {
        <button type="button" class="btn btn-outline-success btn-sm" (click)="addRow()">
          <i class="bi bi-plus-lg"></i> Eintrag
        </button>
        <label class="btn btn-outline-secondary btn-sm mb-0"
               title="Ersetzt die Liste durch ein hochgeladenes JSON-Array (Massenpflege).">
          <i class="bi bi-upload"></i> Import (JSON)
          <input type="file" accept="application/json,.json" hidden (change)="onImportFile($event)">
        </label>
      }
      <span class="text-muted small ms-auto">
        @if (filteredCount > view.length) {
          zeige {{ view.length }} von {{ filteredCount }} – mit Suche eingrenzen
        } @else {
          {{ filteredCount }} Einträge
        }
      </span>
    </div>

    <div class="table-responsive gle-scroll">
      <table class="table table-sm table-hover align-middle mb-0">
        @if (!isStringList) {
          <thead class="sticky-top bg-light">
            <tr>
              @for (c of columns; track c) { <th>{{ c }}</th> }
              @if (!readonly) { <th class="text-end" style="width: 1%;"></th> }
            </tr>
          </thead>
        }
        <tbody>
          @for (row of view; track row.index) {
            <tr>
              @if (isStringList) {
                <td>
                  <input type="text" class="form-control form-control-sm"
                         [ngModel]="row.item" (ngModelChange)="setString(row.index, $event)"
                         [readonly]="readonly">
                </td>
              } @else {
                @for (c of columns; track c) {
                  <td>
                    <input type="text" class="form-control form-control-sm"
                           [ngModel]="row.item[c]" (ngModelChange)="setCell(row.item, c, $event)"
                           [readonly]="readonly">
                  </td>
                }
              }
              @if (!readonly) {
                <td class="text-end">
                  <button type="button" class="btn btn-outline-danger btn-sm"
                          (click)="deleteRow(row.index)" title="Eintrag löschen">
                    <i class="bi bi-trash"></i>
                  </button>
                </td>
              }
            </tr>
          } @empty {
            <tr>
              <td [attr.colspan]="(columns.length || 1) + (readonly ? 0 : 1)"
                  class="text-muted text-center py-3">
                Keine Einträge
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  `,
})
export class GenericListEditorComponent implements OnChanges {
  /** Das zu editierende Array (wird IN PLACE mutiert). */
  @Input() data: any[] = [];
  /** Nur-Lese-Modus (keine Bearbeitung). */
  @Input() readonly = false;
  /** Feuert nach jeder Änderung (Add/Edit/Delete/Import). */
  @Output() changed = new EventEmitter<void>();

  /** Maximale Anzahl gleichzeitig gerenderter Zeilen (Performance bei Großkatalogen). */
  readonly RENDER_CAP = 200;

  columns: string[] = [];
  isStringList = false;
  numericCols = new Set<string>();

  searchTerm = '';
  filteredCount = 0;
  view: { item: any; index: number }[] = [];

  ngOnChanges(): void {
    this.recompute();
  }

  /** Inferiert Spalten + Listentyp + numerische Spalten aus den Daten. */
  private inferShape(): void {
    if (this.data.length === 0) {
      this.isStringList = true;
      this.columns = [];
      this.numericCols.clear();
      return;
    }
    this.isStringList = this.data.every(x => typeof x !== 'object' || x === null);
    if (this.isStringList) {
      this.columns = [];
      this.numericCols.clear();
      return;
    }
    const cols: string[] = [];
    const seen = new Set<string>();
    for (const row of this.data) {
      if (row && typeof row === 'object') {
        for (const k of Object.keys(row)) {
          if (!seen.has(k)) { seen.add(k); cols.push(k); }
        }
      }
    }
    this.columns = cols;
    // Eine Spalte gilt als numerisch, wenn jeder gesetzte Wert eine Zahl ist
    // (und mindestens einer existiert) — dann wird beim Edit zurückgecastet.
    this.numericCols = new Set(
      cols.filter(c =>
        this.data.some(r => typeof r?.[c] === 'number') &&
        this.data.every(r => r?.[c] == null || typeof r[c] === 'number')
      )
    );
  }

  /** Baut die gefilterte + gekappte Sicht neu auf. */
  recompute(): void {
    this.inferShape();
    const term = this.searchTerm.trim().toLowerCase();
    const all = this.data.map((item, index) => ({ item, index }));
    const filtered = term ? all.filter(r => this.rowMatches(r.item, term)) : all;
    this.filteredCount = filtered.length;
    this.view = filtered.slice(0, this.RENDER_CAP);
  }

  private rowMatches(item: any, term: string): boolean {
    if (item == null) return false;
    if (typeof item !== 'object') return String(item).toLowerCase().includes(term);
    return this.columns.some(c => String(item[c] ?? '').toLowerCase().includes(term));
  }

  setString(index: number, value: string): void {
    this.data[index] = value;
    this.changed.emit();
  }

  setCell(item: any, col: string, value: string): void {
    item[col] = this.numericCols.has(col) ? (value === '' ? null : Number(value)) : value;
    this.changed.emit();
  }

  addRow(): void {
    if (this.isStringList) {
      this.data.unshift('');
    } else {
      const o: any = {};
      for (const c of this.columns) o[c] = this.numericCols.has(c) ? 0 : '';
      this.data.unshift(o);
    }
    this.searchTerm = '';
    this.changed.emit();
    this.recompute();
  }

  deleteRow(index: number): void {
    this.data.splice(index, 1);
    this.changed.emit();
    this.recompute();
  }

  onImportFile(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result));
        if (Array.isArray(parsed)) {
          this.data.length = 0;
          this.data.push(...parsed);
          this.changed.emit();
          this.recompute();
        }
      } catch {
        // Ungültiges JSON wird ignoriert (kein Crash); der Nutzer sieht keine Änderung.
      }
      input.value = '';
    };
    reader.readAsText(file);
  }
}
